import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ApproveIcon } from '../../../assets/icons/action-approve.svg';
import { ReactComponent as RejectIcon } from '../../../assets/icons/action-reject.svg';
import { ReactComponent as DynamicPricingIcon } from '../../../assets/icons/sidebar/dynamic-pricing.svg';
import { brandIcons } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import {
  DynamicPricingProperties,
  RequestPricingInterface,
} from '../../../interface/dynamic-pricing.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disablePendingDynamicPricingFiltersApplied,
  fetchListDynamicPricingFilters,
  fetchPendingNotification,
  fetchPendingPricing,
  updatePendingDynamicPricingFilters,
  setSortData,
} from '../../../redux/slices/PendingPricingSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import dynamicPricing from '../../../utils/api/dynamic-pricing';

import RejectReason from './RejectReason';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  notification: boolean;
}

/**
 * Approved dynamic pricing table
 */
function PendingPricing() {
  const { t } = useTranslation('pricing');
  const dispatch = useAppDispatch();
  const {
    pricing,
    loading,
    page,
    limit,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.pendingPricing);

  const [modalStatus, setModalStatus] = useState<'open' | 'close'>('close');
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    notification: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The data
   */
  const fetchPricing = (data: RequestPricingInterface) => {
    if (pricing.length === 0 || !loading) {
      dispatch(fetchPendingPricing(data));
    }
  };

  /**
   * Function to update the status of pricing
   * @param {any} id - The id
   */
  const handleApprovePricing = (id: string) => {
    dynamicPricing
      .updateDynamicPricing(id, { status: 'approved' })
      .then(() => {
        setModalStatus('open');
        setModalView({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('approvedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                fetchPricing({
                  limit,
                  page,
                });
                dispatch(fetchPendingNotification());
                setModalStatus('close');
              }}
            />
          ),
          notification: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Reject pricing API function
   * @param {any} id - The id
   * @param {any} reason - The reason
   */
  const handleRejectPricingAPI = (id: string, reason: string | number) => {
    dynamicPricing
      .updateDynamicPricing(id, { status: 'rejected', reason })
      .then(() => {
        setModalStatus('open');
        setModalView({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('pricingRejected')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                fetchPricing({
                  limit,
                  page,
                });
                dispatch(fetchPendingNotification());
                setModalStatus('close');
              }}
            />
          ),
          notification: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to update the status of pricing
   * @param {any} id - The id
   */
  const handleRejectPricing = (id: string) => {
    setModalStatus('open');
    setModalView({
      title: '',
      header: false,
      content: (
        <RejectReason
          stationId={id}
          onSubmit={handleRejectPricingAPI}
          onClose={() => setModalStatus('close')}
        />
      ),
      notification: false,
    });
  };

  /**
   * Renders the custom data for the actions column
   * @param {any} pricingData - The pricingData data
   * @returns {any} The rendered actions cell
   */
  const renderActions = (pricingData: DynamicPricingProperties) => (
    <div className="action-button-group">
      <div
        className="action-button-item view-color"
        onClick={() => handleApprovePricing(pricingData._id)}
        role="presentation">
        <ApproveIcon />
      </div>
      <div
        className="action-button-item view-color"
        onClick={() => handleRejectPricing(pricingData._id)}
        role="presentation">
        <RejectIcon />
      </div>
    </div>
  );

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof DynamicPricingProperties,
      sortAllow: true,
    },
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof DynamicPricingProperties,
      sortAllow: true,
    },
    {
      label: t('electricityPrice'),
      accessorKey: 'electricityCostByUtilityProvider' as keyof DynamicPricingProperties,
      sortAllow: true,
      /**
       * Electricity price
       * @param {any} rowData - The rowData
       */
      customData: (rowData: DynamicPricingProperties) => (
        <p>
          {rowData?.electricityCostByUtilityProvider
            ? `${rowData.electricityCostByUtilityProvider}/-`
            : 'Not Available'}
        </p>
      ),
    },
    {
      label: t('baseRate'),
      customLabel: (
        <div className="dynamic-pricing-custom-label">
          <span>
            <brandIcons.currencyIcon />
          </span>
          <span>{t('baseRate')}</span>
        </div>
      ),
      accessorKey: 'base' as keyof DynamicPricingProperties,
      /**
       * Document view in table
       * @param {any} rowData - The rowData
       */
      customData: (rowData: DynamicPricingProperties) => <p>{rowData.base}/-</p>,
      sortAllow: true,
    },
    {
      label: t('peakRate'),
      customLabel: (
        <div className="dynamic-pricing-custom-label">
          <span>
            <brandIcons.currencyIcon />
          </span>
          <span>{t('peakRate')}</span>
        </div>
      ),
      accessorKey: 'peak' as keyof DynamicPricingProperties,
      /**
       * Document view in table
       * @param {any} rowData - The rowData
       */
      customData: (rowData: DynamicPricingProperties) => <p>{rowData.peak}/-</p>,
      sortAllow: true,
    },
    {
      label: t('offPeakRate'),
      customLabel: (
        <div className="dynamic-pricing-custom-label">
          <span>
            <brandIcons.currencyIcon />
          </span>
          <span>{t('offPeakRate')}</span>
        </div>
      ),
      accessorKey: 'offpeak' as keyof DynamicPricingProperties,
      /**
       * Document view in table
       * @param {any} rowData - The rowData
       */
      customData: (rowData: DynamicPricingProperties) => <p>{rowData.offpeak}/-</p>,
      sortAllow: true,
    },
    {
      label: t('action'),
      accessorKey: '' as keyof DynamicPricingProperties,
      /**
       * Function for action button
       * @param station
       * @param index
       */
      customData: renderActions,
      sortAllow: true,
      columnWidth: 60,
    },
  ];

  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('stationName'), accessorKey: 'name' },
    { label: t('electricityPrice'), accessorKey: 'electricityCostByUtilityProvider' },
    { label: t('baseRate'), accessorKey: 'base' },
    { label: t('peakRate'), accessorKey: 'peak' },
    { label: t('offPeakRate'), accessorKey: 'offpeak' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: RequestPricingInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchPricing({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter clear function
   */
  const handleClearFilter = () => {
    fetchPricing({
      page: DefaultApiParameters.page,
      limit,
      status: 'approved',
    });
  };

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      status: 'pending',
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);
    dynamicPricing
      .fetchDynamicPricingExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: DynamicPricingProperties, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDate(row.createdAt) : 'N/A';

              case 'name':
                return row.name || 'N/A';

              case 'electricityCostByUtilityProvider':
                return row.electricityCostByUtilityProvider
                  ? `${row.electricityCostByUtilityProvider}`
                  : 'N/A';

              case 'base':
                return row.base || '0';

              case 'peak':
                return row.peak || '0';

              case 'offpeak':
                return row.offpeak || '0';

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'pending-dynamic-pricing-list.csv');
          } else {
            exportPDF(headers, rows, 'pending-dynamic-pricing-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPricing({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchPricing({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || pricing.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPricing({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePendingDynamicPricingFiltersApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('dynamicPricingApproval'),
        icon: <DynamicPricingIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  return (
    <>
      <ModalBox
        open={modalStatus === 'open'}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalStatus('close')}
        header={modalView.header}
        notification={modalView.notification}
        rightCorner=""
      />
      <div className="dynamic-pricing-table">
        <CustomTable
          header={column}
          data={pricing}
          version="v2"
          serialNo
          filterClearFunction={handleClearFilter}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          height={window.innerHeight >= 700 ? 390 : 397}
          filters={filters}
          filtersToDispatch={updatePendingDynamicPricingFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListDynamicPricingFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refreshRequired
          refershCurrentPages={handleRefresh}
          handleRefresh={handleRefresh}
          hoverLabel="Refresh"
        />
      </div>
    </>
  );
}

export default PendingPricing;
